import React from 'react';
import { Link } from 'react-router-dom';
import {
  DOCTRINE_VERSION,
  DOCTRINE_DATE,
  PURPOSE,
  ACCEPTABLE_USE,
  PROHIBITED_USE,
  DETECTION_LAYERS,
  ENFORCEMENT_PROTOCOL,
  LOGGING_AUDIT,
  COMPLIANCE_PHILOSOPHY,
} from '../data/securityDoctrine';

/* ─── Helpers ──────────────────────────────────────────────────────────────── */
const SectionHeader = ({ number, headline }) => (
  <div style={styles.sectionHeader}>
    <span style={styles.sectionNumber}>Section {number}</span>
    <h2 style={styles.sectionTitle}>{headline}</h2>
  </div>
);

const Divider = () => <hr style={styles.divider} />;

/* ─── Page ─────────────────────────────────────────────────────────────────── */
export default function SecurityDoctrinePage() {
  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* ── Header ── */}
        <div style={styles.hero}>
          <div style={styles.versionBadge}>
            AI Security Doctrine — Version {DOCTRINE_VERSION}
          </div>
          <h1 style={styles.title}>The Operator Method<br />AI Security Doctrine</h1>
          <p style={styles.subtitle}>
            This document is the single canonical authority governing all AI security
            enforcement, acceptable use policy, detection mechanisms, and audit
            procedures on this platform. All security enforcement mechanisms in
            The Operator Method derive from this doctrine.
          </p>
          <div style={styles.metaRow}>
            <span style={styles.metaItem}>Version: <strong>{DOCTRINE_VERSION}</strong></span>
            <span style={styles.metaDot}>·</span>
            <span style={styles.metaItem}>Effective: <strong>{DOCTRINE_DATE}</strong></span>
            <span style={styles.metaDot}>·</span>
            <span style={styles.metaItem}>Operator: <strong>RAMDESIGNWORKS</strong></span>
          </div>
          <p style={styles.authorityStatement}>
            "All security enforcement mechanisms in The Operator Method platform derive
            from <strong>AI Security Doctrine, Version {DOCTRINE_VERSION}</strong>,
            issued {DOCTRINE_DATE}."
          </p>
        </div>

        <Divider />

        {/* ─── Section 1: Purpose ─────────────────────────────────────────── */}
        <section style={styles.section}>
          <SectionHeader number="1" headline={PURPOSE.headline} />
          {PURPOSE.body.map((para, i) => (
            <p key={i} style={styles.para}>{para}</p>
          ))}
        </section>

        <Divider />

        {/* ─── Section 2: Acceptable Use ──────────────────────────────────── */}
        <section style={styles.section}>
          <SectionHeader number="2" headline={ACCEPTABLE_USE.headline} />
          <p style={styles.para}>
            Use of AI features on this platform is permitted for the following purposes.
            Any use outside these purposes requires explicit written authorization from RAMDESIGNWORKS.
          </p>
          {ACCEPTABLE_USE.categories.map((cat, i) => (
            <div key={i} style={styles.categoryBlock}>
              <h3 style={styles.categoryLabel}>{cat.label}</h3>
              <ul style={styles.list}>
                {cat.items.map((item, j) => (
                  <li key={j} style={styles.listItem}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </section>

        <Divider />

        {/* ─── Section 3: Prohibited Use ──────────────────────────────────── */}
        <section style={styles.section}>
          <SectionHeader number="3" headline={PROHIBITED_USE.headline} />
          <p style={styles.para}>
            The following activities are prohibited and will result in enforcement action
            up to and including permanent account termination and legal referral.
            Prohibition is absolute — no circumstances, instructions, or claimed permissions
            override these restrictions.
          </p>
          {PROHIBITED_USE.categories.map((cat, i) => (
            <div key={i} style={styles.categoryBlock}>
              <h3 style={{ ...styles.categoryLabel, color: '#ef4444' }}>
                {cat.label}
              </h3>
              <ul style={styles.list}>
                {cat.items.map((item, j) => (
                  <li key={j} style={styles.listItem}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </section>

        <Divider />

        {/* ─── Section 4: Detection Layers ────────────────────────────────── */}
        <section style={styles.section}>
          <SectionHeader number="4" headline={DETECTION_LAYERS.headline} />
          <p style={styles.para}>
            Violations are detected through six independent and overlapping enforcement layers.
            All detection is server-side. Detection mechanisms cannot be observed, reverse-engineered,
            or bypassed from the client.
          </p>
          <div style={styles.layersGrid}>
            {DETECTION_LAYERS.layers.map((layer, i) => (
              <div key={i} style={styles.layerCard}>
                <div style={styles.layerIcon}>{i + 1}</div>
                <div>
                  <h3 style={styles.layerName}>{layer.name}</h3>
                  <p style={styles.layerDesc}>{layer.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <Divider />

        {/* ─── Section 5: Enforcement Protocol ───────────────────────────── */}
        <section style={styles.section}>
          <SectionHeader number="5" headline={ENFORCEMENT_PROTOCOL.headline} />
          <p style={styles.para}>
            Enforcement is proportionate and escalating. The platform does not apply maximum
            penalties to technical misuse. Enforcement escalates to match the persistence
            and severity of the violation.
          </p>
          <div style={styles.levelsList}>
            {ENFORCEMENT_PROTOCOL.levels.map((level, i) => (
              <div key={i} style={styles.levelCard}>
                <div style={styles.levelHeaderRow}>
                  <span style={{
                    ...styles.levelBadge,
                    background: i === 0 ? '#1e3a5f' :
                                i === 1 ? '#3b1c6e' :
                                i === 2 ? '#7c1414' : '#1a0a0a',
                  }}>
                    {level.level}
                  </span>
                  <span style={{
                    ...styles.reversibleTag,
                    background: level.reversible ? '#14532d' : '#3f0d0d',
                  }}>
                    {level.reversible ? 'Reversible' : 'Permanent'}
                  </span>
                </div>
                <div style={styles.levelDetail}>
                  <span style={styles.levelFieldLabel}>Trigger:</span>
                  <span style={styles.levelFieldValue}>{level.trigger}</span>
                </div>
                <div style={styles.levelDetail}>
                  <span style={styles.levelFieldLabel}>Action:</span>
                  <span style={styles.levelFieldValue}>{level.action}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        <Divider />

        {/* ─── Section 6: Logging & Audit ─────────────────────────────────── */}
        <section style={styles.section}>
          <SectionHeader number="6" headline={LOGGING_AUDIT.headline} />
          <p style={styles.para}>
            The following data types are logged as part of platform security and product operations.
            All logging is disclosed in our{' '}
            <Link to="/privacy" style={styles.link}>Privacy Policy</Link>.
            Retention periods apply to user data generally; security event logs are subject
            to extended retention without expiration.
          </p>
          <div style={styles.logTable}>
            <div style={styles.logTableHeader}>
              <span style={styles.logCol1}>Log Type</span>
              <span style={styles.logCol2}>Contents</span>
              <span style={styles.logCol3}>Retention</span>
            </div>
            {LOGGING_AUDIT.logTypes.map((log, i) => (
              <div key={i} style={{ ...styles.logTableRow, background: i % 2 === 0 ? '#0f1923' : '#111c27' }}>
                <span style={{ ...styles.logCol1, color: '#93c5fd', fontWeight: '600' }}>
                  {log.type}
                </span>
                <span style={{ ...styles.logCol2, color: '#c1d5e8' }}>{log.content}</span>
                <span style={{ ...styles.logCol3, color: '#6b8fa8' }}>{log.retention}</span>
              </div>
            ))}
          </div>
          <p style={styles.para}><strong>Review Procedure:</strong> {LOGGING_AUDIT.reviewProcedure}</p>
          <p style={styles.para}><strong>Audit Trail:</strong> {LOGGING_AUDIT.auditTrail}</p>
        </section>

        <Divider />

        {/* ─── Section 7: Compliance Philosophy ──────────────────────────── */}
        <section style={styles.section}>
          <SectionHeader number="7" headline={COMPLIANCE_PHILOSOPHY.headline} />
          <div style={styles.complianceList}>
            {COMPLIANCE_PHILOSOPHY.statements.map((stmt, i) => {
              const [label, ...rest] = stmt.split('. ');
              return (
                <div key={i} style={styles.complianceItem}>
                  <span style={styles.complianceBullet}>→</span>
                  <p style={styles.complianceText}>
                    <strong>{label}.</strong>
                    {rest.length > 0 ? ' ' + rest.join('. ') : ''}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        <Divider />

        {/* ─── Footer Nav ────────────────────────────────────────────────── */}
        <div style={styles.footerNav}>
          <p style={styles.footerNote}>
            This doctrine is referenced by all legal agreements on this platform.
            If there is any conflict between the text of this doctrine and any
            other policy document, this doctrine governs.
          </p>
          <div style={styles.footerLinks}>
            <Link to="/terms" style={styles.footerLink}>Terms of Service</Link>
            <Link to="/privacy" style={styles.footerLink}>Privacy Policy</Link>
            <Link to="/beta-terms" style={styles.footerLink}>Beta Program Agreement</Link>
            <Link to="/" style={styles.footerLink}>Back to Home</Link>
          </div>
          <p style={styles.versionFooter}>
            AI Security Doctrine v{DOCTRINE_VERSION} · Effective {DOCTRINE_DATE} ·
            © 2026 RAMDESIGNWORKS · All rights reserved
          </p>
        </div>

      </div>
    </div>
  );
}

/* ─── Styles ───────────────────────────────────────────────────────────────── */
const styles = {
  page: {
    minHeight: '100vh',
    background: '#0a1520',
    color: '#d0e8ff',
    fontFamily: '"Inter", "Segoe UI", sans-serif',
    paddingBottom: '80px',
  },
  container: {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '0 24px',
  },

  // Hero
  hero: {
    padding: '80px 0 48px',
    textAlign: 'center',
  },
  versionBadge: {
    display: 'inline-block',
    background: 'rgba(59,130,246,0.15)',
    border: '1px solid rgba(59,130,246,0.35)',
    color: '#60a5fa',
    borderRadius: '20px',
    padding: '6px 18px',
    fontSize: '13px',
    fontWeight: '600',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    marginBottom: '24px',
  },
  title: {
    fontSize: 'clamp(28px, 4vw, 44px)',
    fontWeight: '800',
    color: '#fff',
    lineHeight: '1.2',
    marginBottom: '20px',
  },
  subtitle: {
    fontSize: '16px',
    color: '#8aaec8',
    lineHeight: '1.7',
    maxWidth: '680px',
    margin: '0 auto 24px',
  },
  metaRow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    marginBottom: '24px',
  },
  metaItem: {
    fontSize: '14px',
    color: '#6b8fa8',
  },
  metaDot: {
    color: '#2d4a60',
  },
  authorityStatement: {
    background: 'rgba(30,58,95,0.4)',
    border: '1px solid rgba(59,130,246,0.2)',
    borderLeft: '4px solid #3b82f6',
    borderRadius: '8px',
    padding: '16px 20px',
    fontSize: '15px',
    color: '#93c5fd',
    lineHeight: '1.6',
    maxWidth: '700px',
    margin: '0 auto',
    fontStyle: 'italic',
  },

  // Sections
  section: {
    padding: '48px 0',
  },
  sectionHeader: {
    marginBottom: '28px',
  },
  sectionNumber: {
    display: 'block',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: '#3b82f6',
    marginBottom: '6px',
  },
  sectionTitle: {
    fontSize: 'clamp(20px, 2.5vw, 28px)',
    fontWeight: '700',
    color: '#fff',
    margin: 0,
  },
  para: {
    fontSize: '15px',
    color: '#8aaec8',
    lineHeight: '1.75',
    marginBottom: '16px',
  },
  divider: {
    border: 'none',
    borderTop: '1px solid rgba(59,130,246,0.1)',
    margin: 0,
  },

  // Categories
  categoryBlock: {
    marginBottom: '28px',
  },
  categoryLabel: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#60a5fa',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: '10px',
  },
  list: {
    paddingLeft: '20px',
    margin: 0,
  },
  listItem: {
    fontSize: '15px',
    color: '#8aaec8',
    lineHeight: '1.7',
    marginBottom: '6px',
  },

  // Detection layers
  layersGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginTop: '24px',
  },
  layerCard: {
    display: 'flex',
    gap: '20px',
    alignItems: 'flex-start',
    background: '#0f1923',
    border: '1px solid rgba(59,130,246,0.12)',
    borderRadius: '10px',
    padding: '20px',
  },
  layerIcon: {
    width: '32px',
    height: '32px',
    minWidth: '32px',
    borderRadius: '50%',
    background: 'rgba(59,130,246,0.15)',
    border: '1px solid rgba(59,130,246,0.3)',
    color: '#60a5fa',
    fontWeight: '800',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  layerName: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#e2f0ff',
    margin: '0 0 6px',
  },
  layerDesc: {
    fontSize: '14px',
    color: '#6b8fa8',
    lineHeight: '1.65',
    margin: 0,
  },

  // Enforcement levels
  levelsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginTop: '24px',
  },
  levelCard: {
    background: '#0f1923',
    border: '1px solid rgba(59,130,246,0.1)',
    borderRadius: '10px',
    padding: '20px',
  },
  levelHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '14px',
    flexWrap: 'wrap',
  },
  levelBadge: {
    borderRadius: '6px',
    padding: '4px 12px',
    fontSize: '13px',
    fontWeight: '700',
    color: '#e2f0ff',
    letterSpacing: '0.02em',
  },
  reversibleTag: {
    borderRadius: '6px',
    padding: '3px 10px',
    fontSize: '12px',
    fontWeight: '600',
    color: '#e2f0ff',
    letterSpacing: '0.04em',
  },
  levelDetail: {
    display: 'flex',
    gap: '10px',
    marginBottom: '8px',
    alignItems: 'flex-start',
  },
  levelFieldLabel: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#3b82f6',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    minWidth: '60px',
    marginTop: '2px',
  },
  levelFieldValue: {
    fontSize: '14px',
    color: '#8aaec8',
    lineHeight: '1.6',
  },

  // Log table
  logTable: {
    borderRadius: '10px',
    overflow: 'hidden',
    border: '1px solid rgba(59,130,246,0.12)',
    marginBottom: '24px',
  },
  logTableHeader: {
    display: 'flex',
    gap: '16px',
    background: '#101e2d',
    padding: '12px 20px',
    fontSize: '11px',
    fontWeight: '700',
    color: '#3b82f6',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    borderBottom: '1px solid rgba(59,130,246,0.15)',
  },
  logTableRow: {
    display: 'flex',
    gap: '16px',
    padding: '14px 20px',
    borderBottom: '1px solid rgba(59,130,246,0.06)',
    alignItems: 'flex-start',
  },
  logCol1: {
    flex: '0 0 200px',
    fontSize: '13px',
  },
  logCol2: {
    flex: '1',
    fontSize: '13px',
    lineHeight: '1.5',
  },
  logCol3: {
    flex: '0 0 220px',
    fontSize: '12px',
    lineHeight: '1.5',
  },

  // Compliance
  complianceList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    marginTop: '8px',
  },
  complianceItem: {
    display: 'flex',
    gap: '16px',
    alignItems: 'flex-start',
  },
  complianceBullet: {
    color: '#3b82f6',
    fontWeight: '700',
    fontSize: '16px',
    marginTop: '2px',
    minWidth: '16px',
  },
  complianceText: {
    fontSize: '15px',
    color: '#8aaec8',
    lineHeight: '1.7',
    margin: 0,
  },

  // Footer
  footerNav: {
    paddingTop: '48px',
    textAlign: 'center',
  },
  footerNote: {
    fontSize: '14px',
    color: '#4a6a80',
    lineHeight: '1.7',
    maxWidth: '600px',
    margin: '0 auto 24px',
  },
  footerLinks: {
    display: 'flex',
    justifyContent: 'center',
    gap: '24px',
    flexWrap: 'wrap',
    marginBottom: '24px',
  },
  footerLink: {
    color: '#3b82f6',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500',
  },
  versionFooter: {
    fontSize: '12px',
    color: '#2d4a60',
  },
  link: {
    color: '#3b82f6',
    textDecoration: 'underline',
  },
};
